import { createElement } from 'react';
import { AttachmentFailedFilesSummary } from '../components/AttachmentFailedFilesSummary';
import { UrlsProcessingSummary } from '../components/UrlsProcessingSummary';
import { INotificationMapper } from '../containers/NotificationsHub.container';
import {
  IAccountRechargedNotification,
  IApproachingToCostLimitNotification,
  IAttachmentsUploadNotification,
  IBatchAnswerQuestionsFailedNotification,
  ICalculatedCostsLowerThanExpectedNotification,
  IClarifiedNotification,
  IDatasetUploadAbortedNotification,
  IDatasetUploadCompletedNotification,
  IDatasetUploadCompletedWithInfoNotification,
  IDatasetUploadFailedNotification,
  IFailingJobsNotification,
  IHubNotification,
  IJobNotification,
  ILongLivingInstancesNotification,
  IProjectExportFinishedNotification,
  NotificationLevel,
  NotificationType,
} from '../models/notification.model';

export const toNotification = (hubNotification: IHubNotification): INotificationMapper => {
  switch (hubNotification.type) {
    case NotificationType.DatasetUploadCompleted:
      return getDatasetUploaded(hubNotification as IDatasetUploadCompletedNotification);

    case NotificationType.DatasetCompletedWithInfo:
      return getDatasetUploadedWithInfo(hubNotification as IDatasetUploadCompletedWithInfoNotification);

    case NotificationType.DatasetUploadFailed:
      return getDatasetUploadFailed(hubNotification as IDatasetUploadFailedNotification);

    case NotificationType.DatasetUploadAborted:
      return getDatasetUploadAborted(hubNotification as IDatasetUploadAbortedNotification);

    case NotificationType.PaymentPending:
      return getPaymentPending(hubNotification);

    case NotificationType.PaymentFailed:
      return getPaymentFailed(hubNotification);

    case NotificationType.AccountRecharged:
      return getAccountRecharged(hubNotification as IAccountRechargedNotification);

    case NotificationType.AttachmentsUploadCompleted:
      return getAttachmentsUploaded(hubNotification as IAttachmentsUploadNotification);

    case NotificationType.AttachmentsUploadFailed:
      return getAttachmentsUploadFailed(hubNotification as IAttachmentsUploadNotification);

    case NotificationType.SomeAttachmentsUploadFailed:
      return getSomeAttachmentsUploadFailed(hubNotification as IAttachmentsUploadNotification);

    case NotificationType.BatchAnswerQuestionsFinished:
      return getBatchAnswerQuestionsFinished(hubNotification);

    case NotificationType.BatchAnswerQuestionsFailed:
      return getBatchAnswerQuestionsFailed(hubNotification as IBatchAnswerQuestionsFailedNotification);

    case NotificationType.Clarified:
      return getClarified(hubNotification as IClarifiedNotification);

    case NotificationType.ProjectExportFinished:
      return getProjectExportFinished(hubNotification as IProjectExportFinishedNotification);

    case NotificationType.ApproachingToCostLimit:
      return getApproachingCostLimit(hubNotification as IApproachingToCostLimitNotification);

    case NotificationType.JobFinished:
      return getJobFinished(hubNotification as IJobNotification);

    case NotificationType.JobRetrying:
      return getJobRetrying(hubNotification as IJobNotification);

    case NotificationType.JobFailed:
      return getJobFailed(hubNotification as IJobNotification);

    case NotificationType.JobLongRunTime:
      return getJobLongRunTime(hubNotification as IJobNotification);

    case NotificationType.LongLivingInstances:
      return getLongLivingInstances(hubNotification as ILongLivingInstancesNotification);

    case NotificationType.CalculatedCostsLowerThanExpected:
      return getCalculatedCostsLowerThanExpected(hubNotification as ICalculatedCostsLowerThanExpectedNotification);

    case NotificationType.FailingJobsNotification:
      return getFailingJobs(hubNotification as IFailingJobsNotification);
    default:
      throw 'Not supported notification type';
  }
};

function getDatasetUploaded(notification: IDatasetUploadCompletedNotification): INotificationMapper {
  return {
    title: { template: 'dataset_upload_completed' },
    body: { template: 'upload_of_x_completed', data: { datasetName: notification.datasetName } },
    level: NotificationLevel.SUCCESS,
    wasRead: notification.wasRead,
  };
}

function getAttachmentsUploaded(notification: IAttachmentsUploadNotification): INotificationMapper {
  return {
    title: { template: 'attachment_uploads_have_finished' },
    body: { template: 'upload_of_x_files_completed', data: { count: notification.uploadedCount } },
    level: NotificationLevel.SUCCESS,
    wasRead: notification.wasRead,
  };
}

function getAttachmentsUploadFailed(notification: IAttachmentsUploadNotification): INotificationMapper {
  return {
    title: { template: 'attachment_uploads_have_failed' },
    body: createElement(AttachmentFailedFilesSummary, {
      failedFiles: notification.failedFiles,
      uploadedCount: notification.uploadedCount,
    }),
    level: NotificationLevel.ERROR,
    wasRead: notification.wasRead,
  };
}

function getSomeAttachmentsUploadFailed(notification: IAttachmentsUploadNotification): INotificationMapper {
  return {
    title: { template: 'some_attachments_upload_failed' },
    body: createElement(AttachmentFailedFilesSummary, {
      failedFiles: notification.failedFiles,
      uploadedCount: notification.uploadedCount,
    }),
    level: NotificationLevel.WARNING,
    wasRead: notification.wasRead,
  };
}

function getDatasetUploadedWithInfo(notification: IDatasetUploadCompletedWithInfoNotification): INotificationMapper {
  return {
    level: NotificationLevel.WARNING,
    title: { template: 'dataset_upload_completed' },
    body: createElement(UrlsProcessingSummary, {
      datasetName: notification.datasetName,
      rejectedUrls: notification.rejectedUrls,
    }),
    wasRead: notification.wasRead,
  };
}

function getDatasetUploadAborted(notification: IDatasetUploadAbortedNotification): INotificationMapper {
  return {
    title: { template: 'dataset_upload_was_aborted' },
    body: { template: 'upload_of_x_aborted', data: { datasetName: notification.datasetName } },
    level: NotificationLevel.WARNING,
    wasRead: notification.wasRead,
  };
}

function getDatasetUploadFailed(notification: IDatasetUploadFailedNotification): INotificationMapper {
  return {
    title: { template: 'dataset_upload_failed' },
    body: { template: 'upload_of_x_failed', data: { datasetName: notification.datasetName } },
    level: NotificationLevel.ERROR,
    wasRead: notification.wasRead,
  };
}

function getPaymentPending(notification: IHubNotification): INotificationMapper {
  return {
    title: { template: 'payment_pending' },
    body: {
      template: 'payment_pending_body',
      data: {},
    },
    level: NotificationLevel.WARNING,
    wasRead: notification.wasRead,
  };
}

function getPaymentFailed(notification: IHubNotification): INotificationMapper {
  return {
    title: { template: 'payment_failed' },
    body: {
      template: 'payment_failed_body',
      data: {},
    },
    level: NotificationLevel.ERROR,
    wasRead: notification.wasRead,
  };
}

function getAccountRecharged(notification: IAccountRechargedNotification): INotificationMapper {
  return {
    title: { template: 'account_recharged' },
    body: {
      template: 'account_recharged_body',
      data: { credits: notification.credits, newLimit: notification.newLimit },
    },
    level: NotificationLevel.SUCCESS,
    wasRead: notification.wasRead,
  };
}

function getBatchAnswerQuestionsFinished(notification: IHubNotification): INotificationMapper {
  return {
    title: { template: 'batch_answer_questions_completed_notification_title' },
    body: {
      template: 'batch_answer_questions_completed_notification_body',
      data: {},
    },
    level: NotificationLevel.SUCCESS,
    wasRead: notification.wasRead,
  };
}

function getBatchAnswerQuestionsFailed(notification: IBatchAnswerQuestionsFailedNotification): INotificationMapper {
  return {
    title: { template: 'batch_answer_questions_failed_notification_title' },
    body: {
      template: 'batch_answer_questions_failed_notification_body',
      data: {},
    },
    level: NotificationLevel.ERROR,
    wasRead: notification.wasRead,
  };
}

function getClarified(notification: IClarifiedNotification): INotificationMapper {
  return {
    title: { template: 'clarifications.notifications.clarification_answered.title' },
    body: {
      template: 'clarifications.notifications.clarification_answered.body',
      data: { projectName: notification.projectName, imageName: notification.imageName },
    },
    level: NotificationLevel.SUCCESS,
    wasRead: notification.wasRead,
  };
}

function getProjectExportFinished(notification: IProjectExportFinishedNotification): INotificationMapper {
  return {
    title: { template: 'new.projects.details.export_data.notification_header' },
    body: {
      template: 'new.projects.details.export_data.notification_body',
      data: { projectName: notification.projectName },
    },
    level: NotificationLevel.SUCCESS,
    wasRead: notification.wasRead,
    exportDataFilter: notification.dataFilterType,
    projectExportId: notification.projectExportId,
  } as INotificationMapper;
}

function getApproachingCostLimit(notification: IApproachingToCostLimitNotification): INotificationMapper {
  return {
    title: {
      template: 'approaching_to_cost_limit:notification_header',
    },
    body: {
      template: 'approaching_to_cost_limit:notification_body',
    },
    level: NotificationLevel.WARNING,
    wasRead: notification.wasRead,
    workspaceId: notification.workspaceId,
  } as INotificationMapper;
}

function getJobFinished(notification: IJobNotification): INotificationMapper {
  return {
    title: {
      template: 'job_finished_notification.notification_header',
      data: {
        jobType: notification.jobType,
      },
    },
    body: {
      template: 'job_finished_notification.notification_body',
      data: {
        jobType: notification.jobType,
        jobName: notification.jobName,
      },
    },
    level: NotificationLevel.SUCCESS,
    wasRead: notification.wasRead,
    workspaceId: notification.workspaceId,
    jobId: notification.jobId,
    jobType: notification.jobType,
    notificationType: notification.type,
  } as INotificationMapper;
}

function getJobRetrying(notification: IJobNotification): INotificationMapper {
  return {
    title: {
      template: 'job_retrying_notification.notification_header',
      data: {
        jobType: notification.jobType,
      },
    },
    body: {
      template: 'job_retrying_notification.notification_body',
      data: {
        jobType: notification.jobType,
        jobName: notification.jobName,
      },
    },
    level: NotificationLevel.WARNING,
    wasRead: notification.wasRead,
    workspaceId: notification.workspaceId,
    jobId: notification.jobId,
    jobType: notification.jobType,
    notificationType: notification.type,
  } as INotificationMapper;
}

function getJobFailed(notification: IJobNotification): INotificationMapper {
  return {
    title: {
      template: 'job_failed_notification.notification_header',
      data: {
        jobType: notification.jobType,
      },
    },
    body: {
      template: 'job_failed_notification.notification_body',
      data: {
        jobType: notification.jobType,
        jobName: notification.jobName,
      },
    },
    level: NotificationLevel.ERROR,
    wasRead: notification.wasRead,
    workspaceId: notification.workspaceId,
    jobId: notification.jobId,
    jobType: notification.jobType,
    notificationType: notification.type,
  } as INotificationMapper;
}

function getJobLongRunTime(notification: IJobNotification): INotificationMapper {
  return {
    title: {
      template: 'job_long_run_time_notification.notification_header',
      data: {
        jobType: notification.jobType,
      },
    },
    body: {
      template: 'job_long_run_time_notification.notification_body',
      data: {
        jobType: notification.jobType,
        jobName: notification.jobName,
      },
    },
    level: NotificationLevel.WARNING,
    wasRead: notification.wasRead,
    workspaceId: notification.workspaceId,
    jobId: notification.jobId,
    jobType: notification.jobType,
    notificationType: notification.type,
  } as INotificationMapper;
}

function getLongLivingInstances(notification: ILongLivingInstancesNotification): INotificationMapper {
  return {
    title: {
      template: 'long_living_instances_notification.notification_header',
    },
    body: {
      template: 'long_living_instances_notification.notification_body',
      data: {
        n: notification.instanceNames.length,
      },
    },
    level: NotificationLevel.WARNING,
    wasRead: notification.wasRead,
    notificationType: notification.type,
  } as INotificationMapper;
}

function getCalculatedCostsLowerThanExpected(notification: ICalculatedCostsLowerThanExpectedNotification): INotificationMapper {
  return {
    title: {
      template: 'calculated_cost_lower_than_expected_notification.notification_header',
    },
    body: {
      template: 'calculated_cost_lower_than_expected_notification.notification_body',
    },
    level: NotificationLevel.WARNING,
    wasRead: notification.wasRead,
    notificationType: notification.type,
  } as INotificationMapper;
}

function getFailingJobs(notification: IFailingJobsNotification): INotificationMapper {
  return {
    title: {
      template: 'failing_jobs_notification.notification_header',
    },
    body: {
      template: 'failing_jobs_notification.notification_body',
      data: {
        n: notification.jobIds.length,
      },
    },
    level: NotificationLevel.WARNING,
    wasRead: notification.wasRead,
    notificationType: notification.type,
  } as INotificationMapper;
}
